import { Component, OnInit, ViewChild } from '@angular/core';
import { ModalController, NavController, NavParams, ToastController } from 'ionic-angular';
import { process, State } from '@progress/kendo-data-query';
import { voyageLogColumns } from './voyage-log-grid.config';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ValidationResult } from '../../../models/projects/validation.models';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { VoyageLogService } from '../../../services/voyage-log/voyage-log.service';
import {
  SignificantPointGridModel,
  SignificantPointSummary,
  VoyageLogAdjustmentEditor,
  VoyageLogSummary
} from '../../../models/voyage-log/voyagelog.models';
import { DateService } from '../../../services/date/date.service';
import { FormControl, FormGroup } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { VoyageLogPermissions } from '../../../models/permissions/permission.models';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { ErrorsPage } from '../../shared/components/errors-page/errors-page';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { ImplementerService } from '../../../services/implementer/implementer.service';
import { CompleteDayService } from '../../../services/complete-day/complete-day.service';
import { CompleteDayFilters } from '../../../models/complete-day.models';
import { AppConfig } from '../../../app/app.config';
import { VoyageLogAdjustmentModalComponent } from '../voyage-log-adjustment-modal/voyage-log-adjustment-modal';
import { ConfirmationModalComponent } from '../../daily-log/confirmation-modal/confirmation-modal.component';

const createFormGroup = dataItem => {
  return new FormGroup({
    'distance': new FormControl(dataItem.distance),
    'startCargoLoad': new FormControl(dataItem.startCargoLoad),
    'adjustment': new FormControl(dataItem.adjustment)
  });
};

@Component({
  selector: 'app-voyage-log-component',
  templateUrl: 'voyage-log-component.html',
})
export class VoyageLogComponent implements OnInit {
  @ViewChild('voyageLogGrid') grid?: GridComponent;

  config = AppConfig.settings;
  loading: boolean;
  initialLoad: boolean;
  validationLoading: boolean;
  validationResult: ValidationResult = new ValidationResult();

  voyageLogData: VoyageLogSummary[] = [];
  columns = voyageLogColumns;
  filteredColumns: any[] = [];
  state: State = {
    take: 30,
    skip: 0,
    sort: [{field: 'voyageNumber', dir: 'desc'}]
  };
  data: GridDataResult;
  editRowId: number;
  rowEditActive: boolean;
  rowEditClone: VoyageLogSummary;
  editedRowFormGroup: FormGroup;
  permissions: VoyageLogPermissions;
  dailyLogDate: PtLuxonDate;
  endOfDay: PtLuxonDate;
  isImplementerActiveForDay: boolean;
  dayCompleted: boolean;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private dailyLogService: DailyLogService,
    private lockedDayService: LockedDayService,
    private voyageLogService: VoyageLogService,
    private dateService: DateService,
    private toastCtrl: ToastController,
    private permissionsService: PermissionsService,
    private implementerService: ImplementerService,
    private completeDayService: CompleteDayService,
    private modalController: ModalController
  ) {
  }

  async ngOnInit() {
    this.loading = true;
    this.initialLoad = true;

    this.permissions = await this.permissionsService.voyageLogPermissions();
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    this.endOfDay = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);

    await Promise.all([
      this.checkImplementerActive(),
      this.checkDayCompleted()
    ]);

    this.updateVoyageLogData();
  }

  ionViewDidLoad() {

  }

  async updateVoyageLogData() {
    this.voyageLogData = await this.voyageLogService.getAndUpdateVoyageLogData();
    this.loadGrid();
  }


  pageChange({skip, take}: PageChangeEvent): void {
    this.loading = true;
    this.grid.loading = true;
    this.state.skip = skip;
    this.state.take = take;
    this.loadGrid();
  }

  async loadGrid() {
    this.validationLoading = true;
    this.data = process(this.voyageLogData, this.state);

    for (let i = 0; i < this.data.data.length; i++) {
      this.data.data[i].startDateShow = await this.dateService.setFullTimeString(this.data.data[i].startDate);
      this.data.data[i].endDateShow = await this.dateService.setFullTimeString(this.data.data[i].endDate);

      this.setGridDataRowCanEditFlag(i);

      if (this.data.data[i].dateTimeReached) {
        this.data.data[i].dateTimeReachedShow = await this.dateService.setFullTimeString(this.data.data[i].dateTimeReached);
      }
    }

    this.loading = false;
    this.initialLoad = false;
    const tempValidationResult = await this.voyageLogService.validateVoyageLogOnPage(this.data.data, new ValidationResult());
    this.validationResult = tempValidationResult;
    this.validationLoading = false;
    this.grid.loading = false;
  }

  setGridDataRowCanEditFlag(index: number) {
    this.data.data[index].canEditRecord = !this.dayCompleted && (!this.data.data[index].completed
      || (this.data.data[index].endDate >= this.dailyLogDate.dateTime && this.data.data[index].endDate < this.endOfDay.dateTime)
      || (this.config.isImplementerEnvironment && this.isImplementerActiveForDay));
  }

  editRow(row: VoyageLogSummary) {
    this.rowEditActive = true;
    this.rowEditClone = cloneDeep(row);
    const editRowIndex = this.data.data.findIndex(e => e.id === row.id);
    this.editedRowFormGroup = createFormGroup(row);
    this.editRowId = row.id ? row.id : row.localId;
    this.grid.editRow(editRowIndex, this.editedRowFormGroup);
  }

  async checkImplementerActive() {
    this.isImplementerActiveForDay = await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, cloneDeep(this.dailyLogDate));
  }

  async checkDayCompleted() {
    const filters = new CompleteDayFilters();
    filters.date = this.dailyLogDate.dateTime;

    this.dayCompleted = await this.completeDayService.checkDay(filters);
  }

  async saveRow(row: VoyageLogSummary) {
    this.grid.loading = true;
    this.validationLoading = true;
    const editRowIndex = this.data.data.findIndex(e => e.id === row.id);
    const updatedLog = await this.voyageLogService.saveVoyageLog(row);

    if (updatedLog) {
      row.endCargoLoad = updatedLog.endCargoLoad;
      row.completed = updatedLog.completed;
      this.voyageLogService.saveLocalVoyageLog(updatedLog);
      this.showRowSavedToast();
    }

    this.data.data[editRowIndex] = row;
    const tempValidationResult = await this.voyageLogService.validateVoyageLogOnPage(this.data.data, new ValidationResult());
    this.validationResult = tempValidationResult;
    this.grid.closeRow(editRowIndex);
    this.rowEditActive = false;
    this.editRowId = null;
    this.grid.loading = false;
    this.validationLoading = false;
  }

  cancelRowChanges(row: VoyageLogSummary) {
    const editRowIndex = this.data.data.findIndex(e => e.id === row.id);
    this.data.data[editRowIndex] = this.rowEditClone;
    this.grid.closeRow(editRowIndex);
    this.rowEditActive = false;
    this.editRowId = null;
  }

  showRowSavedToast() {
    const toast = this.toastCtrl.create({
      message: 'Voyage Log Saved',
      duration: 3000,
      position: 'bottom'
    });

    toast.present();
  }

  keyPress(event: KeyboardEvent, allowNegative: boolean) {
    if (event.key === '-' && !allowNegative || event.key === '.') {
      event.preventDefault();
    }
  }

  getMinValue(allowNegative: any) {
    return allowNegative ? -9999 : 0;
  }

  async openErrorModal() {
    this.navCtrl.push(ErrorsPage,
      {
        date: null,
        validationResult: this.validationResult,
        overlapTimes: null,
        instances: null,
        completeDay: false,
        refresh: true
      });
  }

  openAdjustmentModal(voyageLogId: number) {
    const modal = this.modalController.create(VoyageLogAdjustmentModalComponent, {
      voyageLogId: voyageLogId,
      dailyLogDate: this.dailyLogDate
    }, {
      cssClass: 'voyage-log-adjustment-modal'
    });

    modal.present();

    modal.onDidDismiss(data => {
      if (data && data.choice && data.updatedLog) {
        const index = this.data.data.findIndex(e => e.id == voyageLogId);

        if (index !== -1) {
          this.data.data[index] = cloneDeep(data.updatedLog);
          this.setGridDataRowCanEditFlag(index);
          this.loadSignificantPointData({index: index, dataItem: this.data.data[index]});
        }
      }
    })
  }

  loadSignificantPointData($event: { index: number; dataItem: VoyageLogSummary }) {
    this.data.data[$event.index].significantPointsLoading = true;
    this.voyageLogService.getSignificantPointDataForLog($event.dataItem.id).subscribe(res => {
      this.data.data[$event.index].significantPointData = res;
      this.data.data[$event.index].significantPointsLoading = false;

      // set row edit clone data, or if user finishes editing while the signif points are open, they will revert on finish
      if (this.rowEditClone && this.rowEditClone.id == $event.dataItem.id) {
        this.rowEditClone.significantPointData = res;
      }
    })
  }

  openDeleteConfirmationModel(adjustmentEditor: SignificantPointGridModel) {
    const confirmationModal = this.modalController.create(ConfirmationModalComponent, {
      title: 'Delete Confirmation',
      comment: `Date/Time: ${adjustmentEditor.dateTimeReachedShow} - Description: ${adjustmentEditor.description}`,
      message: 'Are you sure you want to remove this adjustment?',
      trueText: 'Confirm',
      falseText: 'Cancel',
      showButtons: true
    }, {cssClass: 'daily-log-modal-three-line'});

    confirmationModal.present();

    confirmationModal.onDidDismiss(async data => {
      if (data && data.choice) {
        const index = this.data.data.findIndex(e => e.id == adjustmentEditor.voyageLogId);
        this.data.data[index].significantPointsLoading = true;

        this.voyageLogService.deleteAdjustmentEditor(adjustmentEditor.id).subscribe(res => {
          // replace the voyage log as the updated log may have a different adjustment value now

          if (index !== -1) {
            this.data.data[index] = cloneDeep(res);
            this.setGridDataRowCanEditFlag(index);
            this.loadSignificantPointData({index: index, dataItem: this.data.data[index]});
          }
        });
      }
    })
  }
}



// WEBPACK FOOTER //
// ./src/pages/voyage-log/voyage-log-component/voyage-log-component.ts